export const FIRESTORE_AUTH_READ_MAX_ATTEMPTS = 2;

type AuthTokenUser = {
    uid: string;
    getIdToken: (forceRefresh?: boolean) => Promise<string>;
};

/**
 * Firestore rules that require request.auth.uid throw permission-denied when
 * the ID token is not on the request yet (restored session / token refresh).
 */
export function isFirestorePermissionDenied(error: unknown): boolean {
    const code = String((error as {code?: unknown})?.code || "").toLowerCase();
    const message = String((error as {message?: unknown})?.message || "").toLowerCase();
    return (
        code.includes("permission-denied") ||
        code.includes("unauthenticated") ||
        message.includes("missing or insufficient permissions") ||
        message.includes("permission-denied")
    );
}

/**
 * Attach the current user's ID token, then retry a permission-denied read
 * once after a forced refresh. Wrong-uid denials still fail after the retry.
 */
export async function withFirestoreAuthRetry<T>(
    uid: string,
    read: () => Promise<T>,
    deps: {
        getCurrentUser: () => AuthTokenUser | null;
    }
): Promise<T> {
    const getCurrentUser = deps.getCurrentUser;
    const signedIn = getCurrentUser();
    if (signedIn?.uid === uid) {
        await signedIn.getIdToken(false);
    }

    let lastError: unknown;
    for (let attempt = 1; attempt <= FIRESTORE_AUTH_READ_MAX_ATTEMPTS; attempt++) {
        try {
            return await read();
        } catch (error) {
            lastError = error;
            const retryUser = getCurrentUser();
            if (
                attempt >= FIRESTORE_AUTH_READ_MAX_ATTEMPTS ||
                !isFirestorePermissionDenied(error) ||
                !retryUser ||
                retryUser.uid !== uid
            ) {
                throw error;
            }
            await retryUser.getIdToken(true);
        }
    }
    throw lastError;
}
